import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  ShieldCheck, AlertTriangle, RefreshCw, Plus, Trash2, ClipboardList,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const situacaoBadge = (s) => {
  if (s === 'vencido') return <Badge className="bg-red-100 text-red-800">Vencido</Badge>;
  if (s === 'vencendo') return <Badge className="bg-yellow-100 text-yellow-800">Vencendo</Badge>;
  return <Badge className="bg-green-100 text-green-800">Vigente</Badge>;
};

const ConformidadeTab = () => {
  const [resumo, setResumo] = useState(null);
  const [vencimentos, setVencimentos] = useState([]);
  const [aderencia, setAderencia] = useState({ items: [], has_data: false });
  const [matriz, setMatriz] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reciclando, setReciclando] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ cargo: '', nr_code: '', course_ids: [], observacoes: '' });

  const loadAll = async () => {
    try {
      setLoading(true);
      const [r, v, a, m] = await Promise.all([
        axios.get(`${API}/api/lms/conformidade/resumo`),
        axios.get(`${API}/api/lms/conformidade/vencimentos`),
        axios.get(`${API}/api/lms/matriz/aderencia`),
        axios.get(`${API}/api/lms/matriz`),
      ]);
      setResumo(r.data);
      setVencimentos(v.data || []);
      setAderencia(a.data || { items: [], has_data: false });
      setMatriz(m.data || []);
    } catch (e) {
      toast.error('Erro ao carregar dados de conformidade');
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const res = await axios.get(`${API}/api/lms/courses`);
      setCourses(res.data || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    loadAll();
    loadCourses();
  }, []);

  const handleReciclar = async () => {
    if (!window.confirm('Reinscrever colaboradores com certificados vencidos (cursos com reciclagem automática)?')) return;
    try {
      setReciclando(true);
      const res = await axios.post(`${API}/api/lms/conformidade/reciclar`);
      toast.success(`${res.data?.reinscritos || 0} colaborador(es) reinscrito(s)`);
      loadAll();
    } catch (e) {
      toast.error('Erro ao processar reciclagem');
    } finally {
      setReciclando(false);
    }
  };

  const toggleCourse = (cid) => {
    setForm((prev) => ({
      ...prev,
      course_ids: prev.course_ids.includes(cid)
        ? prev.course_ids.filter((c) => c !== cid)
        : [...prev.course_ids, cid],
    }));
  };

  const handleSaveMatriz = async () => {
    if (!form.cargo.trim()) {
      toast.error('Informe o cargo');
      return;
    }
    if (form.course_ids.length === 0) {
      toast.error('Selecione ao menos um curso obrigatório');
      return;
    }
    try {
      await axios.post(`${API}/api/lms/matriz`, form);
      toast.success('Requisito criado');
      setDialogOpen(false);
      setForm({ cargo: '', nr_code: '', course_ids: [], observacoes: '' });
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar requisito');
    }
  };

  const handleDeleteMatriz = async (id) => {
    if (!window.confirm('Remover este requisito?')) return;
    try {
      await axios.delete(`${API}/api/lms/matriz/${id}`);
      toast.success('Requisito removido');
      loadAll();
    } catch (e) {
      toast.error('Erro ao remover requisito');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Carregando conformidade...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-green-600" /> Conformidade & Reciclagem
          </h2>
          <p className="text-sm text-gray-600">Validade de certificados, matriz por cargo/NR e reciclagem.</p>
        </div>
        <Button onClick={handleReciclar} disabled={reciclando}>
          <RefreshCw className={`h-4 w-4 mr-2 ${reciclando ? 'animate-spin' : ''}`} />
          {reciclando ? 'Processando...' : 'Processar Reciclagem'}
        </Button>
      </div>

      {/* Resumo cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4">
          <div className="text-2xl font-bold">{resumo?.total_com_validade ?? 0}</div>
          <p className="text-sm text-gray-600">Com validade</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-2xl font-bold text-green-600">{resumo?.vigente ?? 0}</div>
          <p className="text-sm text-gray-600">Vigentes</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-2xl font-bold text-yellow-600">{resumo?.vencendo ?? 0}</div>
          <p className="text-sm text-gray-600">Vencendo</p>
        </CardContent></Card>
        <Card><CardContent className="pt-4">
          <div className="text-2xl font-bold text-red-600">{resumo?.vencido ?? 0}</div>
          <p className="text-sm text-gray-600">Vencidos</p>
        </CardContent></Card>
      </div>

      <Tabs defaultValue="vencimentos" className="space-y-4">
        <TabsList>
          <TabsTrigger value="vencimentos">Vencimentos</TabsTrigger>
          <TabsTrigger value="matriz">Matriz NR por Cargo</TabsTrigger>
        </TabsList>

        <TabsContent value="vencimentos">
          {vencimentos.length === 0 ? (
            <Card><CardContent className="text-center py-12">
              <ShieldCheck className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum certificado com validade definida.</p>
            </CardContent></Card>
          ) : (
            <Card><CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Cargo</TableHead>
                    <TableHead>Curso</TableHead>
                    <TableHead>NR</TableHead>
                    <TableHead>Vencimento</TableHead>
                    <TableHead>Dias</TableHead>
                    <TableHead>Situação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vencimentos.map((v) => (
                    <TableRow key={v.certificate_id}>
                      <TableCell>{v.user_name}</TableCell>
                      <TableCell>{v.cargo}</TableCell>
                      <TableCell>{v.course_title}</TableCell>
                      <TableCell>{v.nr_code || '—'}</TableCell>
                      <TableCell>{v.expiry_date ? new Date(v.expiry_date).toLocaleDateString('pt-BR') : '—'}</TableCell>
                      <TableCell>{v.days_left}</TableCell>
                      <TableCell>{situacaoBadge(v.situacao)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          )}
        </TabsContent>

        <TabsContent value="matriz" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <h3 className="font-medium flex items-center gap-2">
              <ClipboardList className="h-4 w-4" /> Aderência por cargo
            </h3>
            <Button size="sm" onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Novo requisito
            </Button>
          </div>

          {!aderencia.has_data || aderencia.items.length === 0 ? (
            <Card><CardContent className="text-center py-12">
              <ClipboardList className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Sem dados de aderência. Cadastre requisitos da matriz.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {aderencia.items.map((item, idx) => (
                <Card key={idx}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-center mb-2">
                      <div>
                        <p className="font-medium">{item.cargo} {item.nr_code ? <Badge variant="outline" className="ml-2">{item.nr_code}</Badge> : null}</p>
                        <p className="text-xs text-gray-500">
                          {item.total_colaboradores} colaborador(es) · {item.cursos_exigidos} curso(s) exigido(s)
                        </p>
                      </div>
                      <div className="text-right">
                        {item.aderencia_percentual === null ? (
                          <span className="text-sm text-gray-400 flex items-center gap-1">
                            <AlertTriangle className="h-4 w-4" /> sem base
                          </span>
                        ) : (
                          <span className="text-lg font-bold">{item.aderencia_percentual}%</span>
                        )}
                      </div>
                    </div>
                    {item.aderencia_percentual !== null && (
                      <Progress value={item.aderencia_percentual} className="h-2" />
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      {item.conformes} conforme(s) · {item.pendentes} pendente(s)
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Lista de requisitos cadastrados */}
          {matriz.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Requisitos cadastrados</CardTitle>
                <CardDescription>Cursos obrigatórios por cargo</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Cargo</TableHead>
                      <TableHead>NR</TableHead>
                      <TableHead>Cursos</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matriz.map((m) => (
                      <TableRow key={m.id}>
                        <TableCell>{m.cargo}</TableCell>
                        <TableCell>{m.nr_code || '—'}</TableCell>
                        <TableCell>{(m.courses || []).map((c) => c.title).join(', ') || '—'}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteMatriz(m.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Matriz dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo requisito de treinamento</DialogTitle>
            <DialogDescription>Cursos obrigatórios para um cargo/NR.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Cargo</Label>
              <Input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} placeholder="Ex.: Técnico de Segurança" />
            </div>
            <div>
              <Label>NR (opcional)</Label>
              <Input value={form.nr_code} onChange={(e) => setForm({ ...form, nr_code: e.target.value })} placeholder="Ex.: NR-35" />
            </div>
            <div>
              <Label>Cursos obrigatórios</Label>
              <div className="border rounded-md max-h-48 overflow-y-auto p-2 space-y-1">
                {courses.length === 0 && <p className="text-sm text-gray-500 p-2">Nenhum curso disponível.</p>}
                {courses.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 p-1 rounded">
                    <Checkbox checked={form.course_ids.includes(c.id)} onCheckedChange={() => toggleCourse(c.id)} />
                    {c.title}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Input value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSaveMatriz}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConformidadeTab;
